import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */

interface State {
  id: number;
  name: string;
}

interface UpdateDistrictProps {
  handleClose: () => void;
  fetchDistricts: () => void;
  districtValues: any;
  selectedVendorId: string;
}

const UpdateDistricts: React.FC<UpdateDistrictProps> = ({
  handleClose,
  fetchDistricts,
  districtValues,
  selectedVendorId
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    vendorId: selectedVendorId || "",
    stateId: "",
    name: "",  // Changed from 'district' to 'name'
    shortName: "",  // Changed from 'shortCode' to 'shortName'
    isActive: true,
    updatedBy: "system"
  });

  /* =========================
     Fetch States
  ========================= */
  useEffect(() => {
    const fetchStates = async () => {
      const vendorIdToUse = selectedVendorId || form.vendorId;

      if (!vendorIdToUse) {
        setStates([]);
        return;
      }

      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/state/vendor/${vendorIdToUse}/findAll`
        );

        setStates(res?.data || res || []);
      } catch {
        showSnackbar("Failed to load states", "error");
      }
    };

    fetchStates();
  }, [APIURL, selectedVendorId, form.vendorId, showSnackbar]);

  /* =========================
     Prefill Data
  ========================= */
  useEffect(() => {
    if (districtValues) {
      setForm({
        id: districtValues.id,
        vendorId: selectedVendorId || districtValues.vendorId || "",
        stateId: districtValues.stateId || "",
        name: districtValues.name || districtValues.district || "",
        shortName: districtValues.shortName || districtValues.shortCode || "",
        isActive: districtValues.isActive !== undefined ? districtValues.isActive : true,
        updatedBy: "system"
      });
    }
  }, [districtValues, selectedVendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.stateId || !form.name) {
      showSnackbar(
        "Vendor, State and District are required",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        vendorId: form.vendorId,
        stateId: Number(form.stateId),
        name: form.name,
        shortName: form.shortName || null,
        isActive: form.isActive,
        updatedBy: form.updatedBy
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/district`,
        payload
      );

      if (response?.statusCode === 200 || response?.statusCode === "200") {
        showSnackbar(
          response?.message || "District updated successfully",
          "success"
        );

        fetchDistricts();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to update district",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(
        error?.message || "Failed to update district",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update District
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor - Display Only */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Vendor"
            value={form.vendorId || "No vendor selected"}
            disabled
            InputProps={{
              readOnly: true
            }}
            helperText="Vendor is selected from parent list"
          />
        </Grid>

        {/* State */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="State"
            name="stateId"
            value={form.stateId}
            onChange={handleChange}
          >
            <MenuItem value="" disabled>
              Select State
            </MenuItem>

            {states.map(state => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* District Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="District Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortName"
            value={form.shortName}
            onChange={handleChange}
          />
        </Grid>

        {/* Active */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Buttons */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !form.vendorId}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpdateDistricts;